import { DBUserSaleContribution } from 'lib/types';
import { useMemo } from 'react';
import * as React from 'react';
import { poolsConfigById } from '~config/poolsConfig';
import { DollarAmount } from '~features/diamondHands/DiamondHandsOverview/DiamondHandsTotalStats';
import {
  DHDistribution,
  DHDistributionStatus,
  DHUserState,
} from '~features/diamondHands/useDHDistribution';
import { DHReward } from '~features/diamondHands/useDHTreasury';
import {
  getUserDHCalculatedRewards,
  mapSaleAddressToPool,
} from '~features/diamondHands/utils';
import { Tooltipped } from '~features/ui';
import { fNum } from '~utils';

export function DHUserRewards({
  dhUser,
  distribution,
  rewards,
  contributions,
}: {
  dhUser: DHUserState;
  distribution: DHDistribution;
  rewards: DHReward[];
  contributions: DBUserSaleContribution[];
}) {
  const userRewards = useMemo(() => {
    return getUserDHCalculatedRewards(distribution, dhUser, rewards);
  }, [dhUser, distribution, rewards]);

  return (
    <div>
      <h5 className="mb-3">Your rewards</h5>

      {distribution.status === DHDistributionStatus.Claiming ? (
        <table className="w-full">
          <thead>
            <tr>
              <td className="w-32">Token</td>
              <td>DH25</td>
              <td>DH75</td>
              <td>Total*</td>
            </tr>
          </thead>
          <tbody>
            {userRewards.map((reward) => {
              const {
                id,
                token,
                token: { imageSrc, symbol, address },
              } = poolsConfigById[reward.id];
              const { isRetroSale, dh75, dh25, total } = reward;

              const contribution = contributions.find((v) => v.poolId === id);

              return (
                <tr key={reward.sale} className="border-b border-gray-800">
                  <td className="flex items-center gap-3">
                    {imageSrc && (
                      <div className="h-8 w-8 rounded-full inline-flex items-center mr-1">
                        <img
                          src={imageSrc}
                          alt={symbol}
                          className="rounded-full"
                        />
                      </div>
                    )}
                    <span>
                      {symbol}{' '}
                      {reward.isRetroSale && (
                        <span className="text-xs text-secondary">(retro)</span>
                      )}
                    </span>
                  </td>
                  <td
                    className={`text-left text-sm ${
                      dh25 > 0 ? 'text-secondary' : 'text-secondary'
                    }`}>
                    {fNum(dh25, 0)}
                  </td>
                  <td
                    className={`text-left text-sm ${
                      dh75 > 0 ? 'text-secondary' : 'text-secondary'
                    }`}>
                    <Tooltipped
                      content={
                        contribution
                          ? `Contributed $${fNum(
                              contribution.contributedTotal,
                              2,
                            )}`
                          : null
                      }
                      className="text-secondary ">
                      {fNum(dh75, 0)}
                    </Tooltipped>
                  </td>
                  <td className="">
                    <div className="flex items-center">
                      <DollarAmount amount={total} token={token} />
                      <span className="text-xs text-secondary ml-2">
                        {fNum(total, 0)} {symbol}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="text-secondary text-sm">
              <td className="w-full pt-4" colSpan={4}>
                * Subject to change during the registration period.
              </td>
            </tr>
          </tfoot>
        </table>
      ) : (
        <p>
          Rewards are split between all registered participants based on their
          share: sale contribution size for DH75, and the staked amount for
          DH25. You will see how much rewards you have once the registration has
          ended for the current distribution. Once registered, your share is
          fixed and cannot be changed.
        </p>
      )}
    </div>
  );
}
